/**
 * Turn a stored baseline fit into `{X, Y}` points for one chart series.
 *
 * The fit is a set of curves indexed by TWS. A chart plots two arbitrary channels against each
 * other, so the curve drawn on it is **parametric in TWS**: walk the shared grid, read X at
 * `(xChannel, xType)` and Y at `(yChannel, yType)`, and emit the pair. That gives a correct line
 * whether the chart is `Bsp vs TWS`, `Bsp vs TWA`, or anything else the builder allows.
 *
 * This is deliberately different from Alinghi's `reduceBaselineLoessPoints`, which reads `row.tws`
 * for X first and only falls back to the x channel. Because every stored row always carries `tws`,
 * that fallback is dead code there and a chart of `Bsp vs TWA` gets a curve whose X values are TWS
 * plotted against a TWA axis. RaceSight's Performance x-axis defaults to TWS but can be `Bsp_kph`
 * or a per-series override, so the parametric form is the only one that stays honest here.
 *
 * It is also deliberately not `reduceTargets`, which fuzzily strips `_target` and `kph|kts|deg|perc`
 * suffixes because target JSON uses short lossy keys. A baseline is keyed by exact
 * `events_aggregate` column names, so matching is exact -- no risk of `Bsp_kph` and `Bsp_tgt_kph`
 * collapsing onto the same base field.
 */

import type {
  BaselineFitJson,
  BaselineFitLeg,
  BaselineLegKey,
} from '../services/baselinesService';

export interface BaselinePoint {
  X: number;
  Y: number;
}

/**
 * One baseline overlay as a chart receives it. Charts take a list of these — one per baseline slot
 * — rather than a single set of flat props, so a second comparison curve costs no new prop surface.
 */
export interface BaselineOverlay {
  fit: BaselineFitJson | null;
  /** Which CONFIG within the fit to draw. A baseline holds one fit per configuration. */
  config: string;
  /** Displayed label. Read only as part of the chart's redraw signature. */
  name?: string;
  color?: string;
  /**
   * Bumped by the display hook whenever this slot's baseline changes. Without it, swapping to a
   * different baseline with the same name and colour would not trigger a repaint.
   */
  revision?: number;
}

/** A contiguous run of points. A coverage gap starts a new segment rather than being bridged. */
export type BaselineSegment = BaselinePoint[];

function normalizeAgr(agrType?: string): string {
  const t = String(agrType || 'AVG').trim().toUpperCase();
  return t === '' ? 'AVG' : t;
}

/**
 * One channel's fitted value at a grid index.
 *
 * Returns null when the channel was never fitted, when it was not fitted at this aggregate type, or
 * when this grid point falls outside the range that channel's own members covered. A missing
 * aggregate bucket deliberately does NOT fall back to AVG: an STD curve silently drawn from AVG
 * numbers would be worse than no curve.
 */
export function readFitValue(
  leg: BaselineFitLeg | null | undefined,
  channel: string,
  agrType: string | undefined,
  index: number
): number | null {
  const buckets = leg?.channels?.[channel];
  if (!buckets) { return null; }
  const series = buckets[normalizeAgr(agrType)];
  if (!series || !Array.isArray(series.values)) { return null; }
  const v = series.values[index];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** True when this leg has a curve for the channel at that aggregate type. */
export function hasFitChannel(
  leg: BaselineFitLeg | null | undefined,
  channel: string,
  agrType?: string
): boolean {
  const buckets = leg?.channels?.[channel];
  return !!buckets && !!buckets[normalizeAgr(agrType)];
}

export interface BaselineSeriesRequest {
  fit: BaselineFitJson | null | undefined;
  config: string;
  leg: BaselineLegKey;
  xChannel: string;
  yChannel: string;
  xType?: string;
  yType?: string;
}

/**
 * The parametric curve for one chart series, split into segments at coverage gaps.
 *
 * Returning segments rather than one array is what keeps a gap visible: d3's `.defined()` would
 * also break the line, but only if the nulls were carried through, and every caller here sorts and
 * filters. Segments make the contract explicit -- each one is a solid stroke.
 */
export function buildBaselineSeries(req: BaselineSeriesRequest): BaselineSegment[] {
  const { fit, config, leg, xChannel, yChannel } = req;
  const grid = fit?.tws_grid;
  if (!fit || !Array.isArray(grid) || grid.length === 0) { return []; }
  if (!xChannel || !yChannel) { return []; }

  const legFit = fit.configs?.[config]?.[leg];
  if (!legFit) { return []; }

  // A chart whose channel this baseline never fitted draws nothing at all, rather than a
  // partial or misleading line.
  if (!hasFitChannel(legFit, xChannel, req.xType) || !hasFitChannel(legFit, yChannel, req.yType)) {
    return [];
  }

  const segments: BaselineSegment[] = [];
  let current: BaselinePoint[] = [];

  for (let i = 0; i < grid.length; i++) {
    const x = readFitValue(legFit, xChannel, req.xType, i);
    const y = readFitValue(legFit, yChannel, req.yType, i);
    if (x == null || y == null) {
      if (current.length > 0) { segments.push(current); current = []; }
      continue;
    }
    current.push({ X: x, Y: y });
  }
  if (current.length > 0) { segments.push(current); }

  // A single point cannot be stroked; drop it rather than emit an invisible segment.
  return segments.filter((s) => s.length >= 2);
}

/**
 * Segments for whichever legs should be drawn.
 *
 * When the page's Uw/Dw split is off, both legs are drawn as separate strokes. Alinghi returns the
 * whole `{UPWIND, DOWNWIND}` object in that case and its chart's `Array.isArray` guard then draws
 * nothing at all -- turning the split off silently loses the baseline, which is not a behaviour
 * worth inheriting.
 */
export function buildBaselineSegments(
  req: Omit<BaselineSeriesRequest, 'leg'> & { leg?: BaselineLegKey | null }
): BaselineSegment[] {
  const legs: BaselineLegKey[] = req.leg ? [req.leg] : ['UPWIND', 'DOWNWIND'];
  return legs.flatMap((leg) => buildBaselineSeries({ ...req, leg }));
}

/** Map the page's Uw/Dw selection onto a fit leg key; null means "no split, draw both". */
export function legKeyFromUwDw(uwDw: string | null | undefined): BaselineLegKey | null {
  const v = String(uwDw || '').trim().toUpperCase();
  if (v === 'UPWIND' || v === 'UW') { return 'UPWIND'; }
  if (v === 'DOWNWIND' || v === 'DW') { return 'DOWNWIND'; }
  return null;
}
